import { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';

/**
 * TabSettings Component
 * 
 * Modal for configuring tab visibility and floorplan settings.
 * Allows users to toggle tabs and upload a floorplan image with cropping functionality.
 */
function TabSettings({ onClose, onApply, currentFloorplan, currentMenu, currentShowVenueTab, currentShowMediaFilter, currentShowGuestbookTab, currentShowQAndATab, onShowToast }) {
    const [floorplanPreview, setFloorplanPreview] = useState(currentFloorplan);
    const [floorplanFile, setFloorplanFile] = useState(null);
    const [menuPreview, setMenuPreview] = useState(currentMenu);
    const [menuFile, setMenuFile] = useState(null);
    const [showVenueTab, setShowVenueTab] = useState(currentShowVenueTab !== false); // Default to true
    const [showMediaFilter, setShowMediaFilter] = useState(currentShowMediaFilter !== false); // Default to true
    const [showGuestbookTab, setShowGuestbookTab] = useState(currentShowGuestbookTab !== false); // Default to true
    const [showQAndATab, setShowQAndATab] = useState(currentShowQAndATab !== false); // Default to true
    const [showCropper, setShowCropper] = useState(false);
    const [imageSrc, setImageSrc] = useState(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

    // Helper to detect if URL is a PDF
    const isPdf = (url) => {
        if (!url) return false;
        return url.toLowerCase().endsWith('.pdf') || url.startsWith('data:application/pdf');
    };

    // Handle floorplan file upload
    const handleFloorplanUpload = (e) => {
        const file = e.target.files[0];
        if (file && file.type.startsWith('image/')) {
            setFloorplanFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImageSrc(reader.result);
                // Set preview directly without cropping
                setFloorplanPreview(reader.result);
                // Optionally show cropper
                // setShowCropper(true);
            };
            reader.readAsDataURL(file);
        }
    };

    // Handle menu file upload (images and PDFs)
    const handleMenuUpload = (e) => {
        const file = e.target.files[0];
        if (file && (file.type.startsWith('image/') || file.type === 'application/pdf')) {
            setMenuFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setMenuPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    // Clear menu
    const handleClearMenu = () => {
        setMenuPreview(null);
        setMenuFile(null);
    };

    // Handle crop complete
    const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    // Create cropped image
    const createCroppedImage = async () => {
        try {
            const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels);
            setFloorplanPreview(croppedImage);
            setShowCropper(false);
        } catch (e) {
            console.error(e);
        }
    };

    // Helper function to crop image (preserves original quality)
    const getCroppedImg = (imageSrc, pixelCrop) => {
        return new Promise((resolve) => {
            const image = new Image();
            image.src = imageSrc;
            image.onload = () => {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');

                // Use the actual pixel dimensions from the crop area
                canvas.width = pixelCrop.width;
                canvas.height = pixelCrop.height;

                ctx.drawImage(
                    image,
                    pixelCrop.x,
                    pixelCrop.y,
                    pixelCrop.width,
                    pixelCrop.height,
                    0,
                    0,
                    pixelCrop.width,
                    pixelCrop.height
                );

                // Use high quality JPEG with 0.95 quality
                canvas.toBlob((blob) => {
                    const reader = new FileReader();
                    reader.readAsDataURL(blob);
                    reader.onloadend = () => {
                        resolve(reader.result);
                    };
                }, 'image/jpeg', 0.95);
            };
        });
    };

    // Handle apply changes
    const handleApply = () => {
        onApply({
            floorplan: floorplanPreview,
            menu: menuPreview,
            showVenueTab: showVenueTab,
            showMediaFilter: showMediaFilter,
            showGuestbookTab: showGuestbookTab,
            showQAndATab: showQAndATab,
            file: floorplanFile,
            menuFile: menuFile,
        });

        // Show success toast
        if (onShowToast) {
            onShowToast({ message: 'Tab settings applied successfully!', type: 'success' });
        }
    };

    // Handle reset to default
    const handleReset = async () => {
        // Import the loader function
        const { loadUserTabsConfig } = require('../utils/userDataLoader');
        const defaults = await loadUserTabsConfig();

        // Update preview only (don't apply or clear localStorage yet)
        setFloorplanPreview(defaults.floorplan);
        setMenuPreview(defaults.menu || null);
        setShowVenueTab(defaults.showVenueTab);
        setShowMediaFilter(defaults.showMediaFilter !== false); // Default to true
        setShowGuestbookTab(defaults.showGuestbookTab !== false); // Default to true
        setShowQAndATab(defaults.showQAndATab !== false); // Default to true
        setFloorplanFile(null);
        setMenuFile(null);
    };

    return (
        <>
            <div
                className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4"
                onClick={(e) => e.target === e.currentTarget && onClose()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="floorplan-picker-title"
            >
                <div className="relative bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                    {/* Header */}
                    <div className="sticky top-0 bg-white border-b border-accent px-6 py-4 flex items-center justify-between z-10">
                        <div>
                            <h2 id="floorplan-picker-title" className="text-2xl font-semibold text-secondary">
                                Tab Configuration
                            </h2>
                            <p className="text-sm text-gray-600 mt-1">
                                Configure tabs and upload floorplan image
                            </p>
                        </div>

                        <button onClick={onClose} aria-label="Close floorplan picker" className="group flex-shrink-0 ml-4">
                            <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 transition-all duration-200 group-hover:bg-gray-200 group-hover:scale-105">
                                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </span>
                        </button>
                    </div>

                    {/* Content */}
                    <div className="px-6 py-6">
                        {/* Tab Configuration Section */}
                        <section className="mb-8">
                            <div className="space-y-4">
                                {/* Show Media Filter Toggle */}
                                <div>
                                    <label className="flex items-center justify-between cursor-pointer">
                                        <div>
                                            <span className="block text-base font-semibold text-secondary">
                                                Show Media Filter Buttons
                                            </span>
                                            <span className="text-sm text-gray-600 mb-4 mt-2">
                                                Toggle to show or hide the ALL/Photos/Videos filter buttons in Gallery
                                            </span>
                                        </div>
                                        <div className="relative ml-4">
                                            <input
                                                type="checkbox"
                                                checked={showMediaFilter}
                                                onChange={(e) => setShowMediaFilter(e.target.checked)}
                                                className="sr-only peer"
                                            />
                                            <div className="w-11 h-6 bg-gray-300 rounded-full peer peer-checked:bg-primary transition-colors"></div>
                                            <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-5"></div>
                                        </div>
                                    </label>
                                </div>

                                {/* Show Venue Tab Toggle */}
                                <div className="border-t-2 border-gray-300 pt-8">
                                    <label className="flex items-center justify-between cursor-pointer">
                                        <div>
                                            <span className="block text-base font-semibold text-secondary">
                                                Show Venue Tab
                                            </span>
                                            <span className="text-sm text-gray-600 mb-4 mt-2">
                                                Toggle to show or hide the venue/floorplan tab on the website
                                            </span>
                                        </div>
                                        <div className="relative ml-4">
                                            <input
                                                type="checkbox"
                                                checked={showVenueTab}
                                                onChange={(e) => setShowVenueTab(e.target.checked)}
                                                className="sr-only peer"
                                            />
                                            <div className="w-11 h-6 bg-gray-300 rounded-full peer peer-checked:bg-primary transition-colors"></div>
                                            <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-5"></div>
                                        </div>
                                    </label>
                                </div>

                                {/* Show Guestbook Tab Toggle */}
                                <div className="border-t-2 border-gray-300 pt-8">
                                    <label className="flex items-center justify-between cursor-pointer">
                                        <div>
                                            <span className="block text-base font-semibold text-secondary">
                                                Show Guestbook Tab
                                            </span>
                                            <span className="text-sm text-gray-600 mb-4 mt-2">
                                                Toggle to show or hide the guestbook tab on the website
                                            </span>
                                        </div>
                                        <div className="relative ml-4">
                                            <input
                                                type="checkbox"
                                                checked={showGuestbookTab}
                                                onChange={(e) => setShowGuestbookTab(e.target.checked)}
                                                className="sr-only peer"
                                            />
                                            <div className="w-11 h-6 bg-gray-300 rounded-full peer peer-checked:bg-primary transition-colors"></div>
                                            <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-5"></div>
                                        </div>
                                    </label>
                                </div>

                                {/* Show Q&A Tab Toggle */}
                                <div className="border-t-2 border-b-2 border-gray-300 pt-8 pb-8">
                                    <label className="flex items-center justify-between cursor-pointer">
                                        <div>
                                            <span className="block text-base font-semibold text-secondary">
                                                Show Q&A Tab
                                            </span>
                                            <span className="text-sm text-gray-600 mb-4 mt-2">
                                                Toggle to show or hide the Q&A tab on the website
                                            </span>
                                        </div>
                                        <div className="relative ml-4">
                                            <input
                                                type="checkbox"
                                                checked={showQAndATab}
                                                onChange={(e) => setShowQAndATab(e.target.checked)}
                                                className="sr-only peer"
                                            />
                                            <div className="w-11 h-6 bg-gray-300 rounded-full peer peer-checked:bg-primary transition-colors"></div>
                                            <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-5"></div>
                                        </div>
                                    </label>
                                </div>

                                {/* Floorplan Upload - Only shown when Venue Tab is enabled */}
                                {showVenueTab && (
                                    <div>
                                        <label className="block text-sm font-medium text-secondary mb-2">
                                            Floorplan Image
                                        </label>
                                        <div className="flex items-center gap-3 flex-wrap">
                                            {floorplanFile && (
                                                <span className="text-sm text-gray-600 flex-1 truncate min-w-0">
                                                    {floorplanFile.name}
                                                </span>
                                            )}
                                            <div className="flex gap-2">
                                                <label htmlFor="floorplan-upload" className="px-4 py-2 bg-primary rounded-lg font-medium hover:opacity-90 transition-all duration-200 shadow-sm cursor-pointer text-sm whitespace-nowrap" style={{color: 'var(--color-surface)'}}>
                                                    <input type="file" id="floorplan-upload" accept="image/png,image/jpeg,image/jpg,image/webp" onChange={handleFloorplanUpload} className="sr-only" />
                                                    {floorplanFile ? 'Change Image' : 'Choose Image'}
                                                </label>
                                            </div>
                                        </div>
                                        <p className="text-sm text-gray-600 mb-4 mt-2">
                                            Upload your floorplan image. The image will be displayed with full width.
                                        </p>
                                    </div>
                                )}

                                {/* Menu Upload - Only shown when Venue Tab is enabled */}
                                {showVenueTab && (
                                    <div className="border-t border-gray-200 pt-4">
                                        <label className="block text-sm font-medium text-secondary mb-2">
                                            Menu Image or PDF (Optional)
                                        </label>
                                        <div className="flex items-center gap-3 flex-wrap">
                                            {menuFile && (
                                                <span className="text-sm text-gray-600 flex-1 truncate min-w-0">
                                                    {menuFile.name}
                                                </span>
                                            )}
                                            <div className="flex gap-2">
                                                <label htmlFor="menu-upload" className="px-4 py-2 bg-primary rounded-lg font-medium hover:opacity-90 transition-all duration-200 shadow-sm cursor-pointer text-sm whitespace-nowrap" style={{color: 'var(--color-surface)'}}>
                                                    <input type="file" id="menu-upload" accept="image/png,image/jpeg,image/jpg,image/webp,application/pdf" onChange={handleMenuUpload} className="sr-only" />
                                                    {menuFile ? 'Change File' : 'Choose File'}
                                                </label>
                                                {menuPreview && (
                                                    <button
                                                        onClick={handleClearMenu}
                                                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-all duration-200 text-sm whitespace-nowrap"
                                                    >
                                                        Clear Menu
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        <p className="text-sm text-gray-600 mb-4 mt-2">
                                            Upload your menu as an image or PDF. This will be displayed below the venue floorplan.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </section>

                        {/* Preview Section - Only shown when Venue Tab is enabled */}
                        {showVenueTab && (
                            <section className="mb-8">
                                <h3 className="text-sm font-medium text-secondary mb-2">
                                    Preview
                                </h3>
                                <p className="text-sm text-gray-600 mb-4 mt-2">
                                    Here's how your floorplan{menuPreview ? ' and menu' : ''} will appear on the website
                                </p>

                                <div className="p-8 bg-gray-50 rounded-lg border-2 border-accent space-y-6">
                                    {/* Floorplan Preview */}
                                    <div className="flex flex-col items-center">
                                        <h4 className="text-sm font-medium text-secondary mb-2">Venue Floorplan</h4>
                                        <img src={floorplanPreview} alt="Floorplan Preview" className="w-full max-w-md object-contain rounded-lg shadow-md" />
                                    </div>

                                    {/* Menu Preview */}
                                    {menuPreview && (
                                        <div className="flex flex-col items-center border-t border-gray-200 pt-6">
                                            <h4 className="text-sm font-medium text-secondary mb-2">Menu</h4>
                                            {isPdf(menuPreview) ? (
                                                <div className="bg-accent-20 rounded-lg p-4 flex items-center gap-3">
                                                    <svg className="w-10 h-10 text-primary-60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                                    </svg>
                                                    <span className="text-secondary">PDF Menu</span>
                                                </div>
                                            ) : (
                                                <img src={menuPreview} alt="Menu Preview" className="w-full max-w-md object-contain rounded-lg shadow-md" />
                                            )}
                                        </div>
                                    )}
                                </div>
                            </section>
                        )}

                        {/* Apply Section */}
                        <section className="border-t-2 border-gray-300 pt-8 mt-8">
                            <h3 className="text-lg font-semibold text-secondary mb-2">
                                Apply Your Settings
                            </h3>
                            <p className="text-sm text-gray-600 mb-4 mt-2">
                                Click "Apply Changes" to preview your settings on the website
                            </p>

                            <div className="space-y-4">
                                {/* Apply and Reset Buttons - One Row */}
                                <div className="flex flex-col sm:flex-row gap-4">
                                    <button
                                        onClick={handleApply}
                                        className="flex-1 px-6 py-3 bg-primary rounded-lg font-medium hover:opacity-90 transition-all duration-200 shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 flex items-center justify-center gap-2" style={{color: 'var(--color-surface)'}}
                                        aria-label="Apply floorplan changes"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                        </svg>
                                        Apply Changes
                                    </button>

                                    <button
                                        onClick={handleReset}
                                        className="flex-1 px-6 py-3 bg-neutral text-secondary rounded-lg font-medium hover:bg-neutral/90 transition-all duration-200 shadow-sm focus:outline-none focus:ring-2 focus:ring-neutral/50 focus:ring-offset-2 focus:ring-offset-white flex items-center justify-center gap-2"
                                        aria-label="Reset to default floorplan"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                        </svg>
                                        Reset to Default
                                    </button>
                                </div>

                                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                    <h4 className="text-sm font-semibold text-blue-900 mb-2 flex items-center gap-2">
                                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                        </svg>
                                        Next Steps
                                    </h4>
                                    <ol className="text-sm text-blue-800 space-y-2 ml-1">
                                        <li className="flex gap-2">
                                            <span className="font-semibold flex-shrink-0">1.</span>
                                            <span>Click "Apply Changes" to preview your settings on the website</span>
                                        </li>
                                        <li className="flex gap-2">
                                            <span className="font-semibold flex-shrink-0">2.</span>
                                            <span>Close this modal and click "Export Config" in the header to save all settings</span>
                                        </li>
                                        <li className="flex gap-2">
                                            <span className="font-semibold flex-shrink-0">3.</span>
                                            <span>Send the exported ZIP file to your developer</span>
                                        </li>
                                        <li className="flex gap-2">
                                            <span className="font-semibold flex-shrink-0">4.</span>
                                            <span>Your developer will deploy your custom configuration</span>
                                        </li>
                                    </ol>
                                </div>
                            </div>
                        </section>
                    </div>
                </div>
            </div>

            {/* Image Cropper Modal */}
            {showCropper && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80 p-4">
                    <div className="relative bg-white rounded-lg shadow-2xl w-full max-w-2xl">
                        <div className="p-6">
                            <h3 className="text-xl font-semibold text-secondary mb-4">Crop Your Floorplan</h3>

                            <div className="relative h-96 bg-gray-100 rounded-lg mb-4">
                                <Cropper
                                    image={imageSrc}
                                    crop={crop}
                                    zoom={zoom}
                                    aspect={0}
                                    onCropChange={setCrop}
                                    onZoomChange={setZoom}
                                    onCropComplete={onCropComplete}
                                    restrictPosition={false}
                                    showGrid={true}
                                />
                            </div>

                            <div className="mb-4">
                                <label className="block text-sm font-medium text-secondary mb-2">
                                    Zoom: {zoom.toFixed(1)}x
                                </label>
                                <input
                                    type="range"
                                    min={0.1}
                                    max={3}
                                    step={0.05}
                                    value={zoom}
                                    onChange={(e) => setZoom(parseFloat(e.target.value))}
                                    className="w-full"
                                />
                                <div className="flex justify-between text-xs text-gray-500 mt-1">
                                    <span>Zoom Out (0.1x)</span>
                                    <span>Zoom In (3x)</span>
                                </div>
                            </div>

                            <div className="flex gap-3">
                                <button
                                    onClick={createCroppedImage}
                                    className="flex-1 px-6 py-3 bg-primary rounded-lg font-medium hover:opacity-90 transition-all duration-200" style={{color: 'var(--color-surface)'}}
                                >
                                    Apply Crop
                                </button>
                                <button
                                    onClick={() => setShowCropper(false)}
                                    className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-all duration-200"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default TabSettings;
